import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/operator/map';
import type { ActionsObservable } from 'redux-observable';
import type { Observable } from 'rxjs/Observable';
import { fg } from '@atlassian/jira-feature-gating';
import type { Action } from '../../../state/actions';
import { SAVE_PROJECT_DETAILS_SUCCESS } from '../../../state/entities/actions';
import { projectKeySelector, projectNameSelector } from '../../../state/entities/selectors';
import type { AppState } from '../../../state/types';
import {
	projectKeyUiSelector,
	projectNameUiSelector,
	hasAvatarChanged,
	projectLeadAccountIdChanged,
	assigneeTypeChanged,
	releasedProjectKeysSelector,
} from '../../../state/ui/selectors';
import type { AnalyticEvent } from '../types';

/**
 * When a project is saved, we want to record that event along with what project
 * information was changed.
 *
 * The most complex part of this epic is the need to record what information has
 * changed before we know the outcome of the request. This is nessessary because
 * by the time we get a success or failure response, the action has been reduced
 * and the ui and entities state is the same.
 */
// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (
	action$: ActionsObservable<Action>,
	store: MiddlewareAPI<AppState>,
): Observable<AnalyticEvent> =>
	action$
		// when someone saves the project
		.ofType(SAVE_PROJECT_DETAILS_SUCCESS)
		// create the analytic event with what information has changed
		.map(() => {
			const state = store.getState();
			return {
				event: 'save',
				data: {
					modifiedProjectKey: projectKeySelector(state) !== projectKeyUiSelector(state),
					modifiedProjectName: projectNameSelector(state) !== projectNameUiSelector(state),
					modifiedProjectAvatar: hasAvatarChanged(state),
					modifiedProjectLead: projectLeadAccountIdChanged(state),
					modifiedDefaultAssignee: assigneeTypeChanged(state),
					modifiedReleasedProjectKeys:
						fg('hix-4189-releasing-historical-project-keys') &&
						releasedProjectKeysSelector(state).length > 0,
				},
			};
		});
